import moment from "moment";
import { CreditAddressAttributes, Name, Tradeline } from "types";
import { CREDIT_REPORT_PAYMENT_TERMS } from "./consts";

export const convertToReadableAddress = (
  address: CreditAddressAttributes,
): string => {
  // key order for the main part of the address
  const mainAddressKeys: (keyof CreditAddressAttributes)[] = [
    "houseNumber",
    "direction",
    "streetName",
    "streetType",
    "unit",
    "city",
  ];

  // Keys for the state and postal code part of the address
  const secondaryAddressKeys: (keyof CreditAddressAttributes)[] = [
    "stateCode",
    "postalCode",
  ];

  const formatAddressPart = (keys: (keyof CreditAddressAttributes)[]) =>
    keys
      .map((key) => address[key])
      .filter(Boolean)
      .join(" ");

  // Combine both parts of the address
  return `${formatAddressPart(mainAddressKeys)}, ${formatAddressPart(
    secondaryAddressKeys,
  )}`;
};

const getAccountTerms = (account: Tradeline) => {
  const grantedTrade = account.GrantedTrade?.[0];
  const paymentFrequency = grantedTrade?.PaymentFrequency?.[0]?.$
    .symbol as keyof typeof CREDIT_REPORT_PAYMENT_TERMS;
  if (
    !grantedTrade ||
    !paymentFrequency ||
    !CREDIT_REPORT_PAYMENT_TERMS[paymentFrequency]
  ) {
    return "";
  }
  return CREDIT_REPORT_PAYMENT_TERMS[paymentFrequency];
};

export const getAccountDetails = (account: Tradeline) => {
  const isCollectionAccount = account.CollectionTrade?.[0] !== undefined;
  return [
    // {
    //   title: "Address",
    //   value: address,
    // },
    // {
    //   title: "Phone",
    //   value: "N/A",
    // },
    {
      title: "Date Opened",
      value: account.$.dateOpened
        ? moment(account.$.dateOpened).format("MM/DD/YYYY")
        : "N/A",
    },
    {
      title: "Responsibility",
      value: account.AccountDesignator?.[0]?.$.description || "N/A",
    },
    {
      title: "Account Type",
      value:
        account.GrantedTrade?.[0]?.CreditType?.[0]?.$.description ||
        account.CollectionTrade?.[0]?.creditType?.[0]?.$.description ||
        "N/A",
    },
    ...(isCollectionAccount
      ? []
      : [
        {
          title: "Loan Type",
          value:
              account.GrantedTrade?.[0]?.AccountType?.[0]?.$.description ||
              "N/A",
        },
      ]),
    ...(isCollectionAccount
      ? [
        {
          title: "Amount Placed",
          value: account.CollectionTrade?.[0]?.$.actualPaymentAmount
            ? `$${account.CollectionTrade?.[0]?.$.actualPaymentAmount}`
            : "N/A",
        },
      ]
      : []),
    ...(isCollectionAccount
      ? [
        {
          title: "Original Client Information",
          value: account.CollectionTrade?.[0]?.$.originalCreditor || "N/A",
        },
      ]
      : []),
    {
      title: "Date updated",
      value: account.$.dateReported
        ? moment(account.$.dateReported).format("MM/DD/YYYY")
        : "N/A",
    },
    {
      title: "Pay Status",
      value: account.PayStatus?.[0]?.$.description || "N/A",
    },
    ...(isCollectionAccount
      ? []
      : [
        {
          title: "Terms",
          value: `Paid ${getAccountTerms(account)}`,
        },
      ]),
    {
      title: "Date Closed",
      value: account.$.dateClosed
        ? moment(account.$.dateClosed).format("MM/DD/YYYY")
        : "N/A",
    },
    {
      title: "High Balance",
      value: account.$.highBalance ? `$${account.$.highBalance}` : "N/A",
    },
    {
      title: "Credit Limit",
      value: account.GrantedTrade?.[0]?.CreditLimit?.[0]
        ? `$${account.GrantedTrade?.[0]?.CreditLimit?.[0]}`
        : "N/A",
    },
    {
      title: "Remarks",
      value: account.Remark?.[0]?.RemarkCode?.[0]?.$.description || "N/A",
    },
  ];
};

export const getReadableNameFromAttributes = (
  nameAttributes: Name["$"] | undefined | null,
) => {
  return `${nameAttributes?.first || ""} ${nameAttributes?.middle || ""} ${
    nameAttributes?.last || ""
  }`.trim();
};
